import { foldService } from '@codemirror/language';
import { StateField, type EditorState } from '@codemirror/state';

/**
 * Scans the full document and returns all block-comment ranges as `[openOffset, closeOffset]`
 * pairs sorted by open offset. `closeOffset` is the position just past `*\/`, or `doc.length` if
 * the comment is unclosed. Ignores `/*` tokens that appear inside `"..."` strings or after `//`.
 */
function computeBlockCommentRanges(
  state: EditorState,
): ReadonlyArray<[number, number]> {
  const text = state.doc.sliceString(0, state.doc.length);
  const ranges: Array<[number, number]> = [];
  let i = 0;
  while (i < text.length) {
    if (text[i] === '"') {
      i = skipStringLiteral(text, i);
    } else if (text[i] === '/' && text[i + 1] === '/') {
      i = skipLineComment(text, i);
    } else if (text[i] === '/' && text[i + 1] === '*') {
      const start = i;
      i = skipBlockComment(text, i);
      ranges.push([start, i]);
    } else {
      i++;
    }
  }
  return ranges;
}

// Recomputed once per transaction (only when the document changes); used by foldService
// to avoid rescanning the entire document once per line (O(N²) → O(N) per transaction).
const blockCommentRangesField = StateField.define<
  ReadonlyArray<[number, number]>
>({
  create: computeBlockCommentRanges,
  update: (value, transaction) =>
    transaction.docChanged
      ? computeBlockCommentRanges(transaction.state)
      : value,
});

/**
 * Returns `true` if `lineStart` falls inside one of the pre-computed block-comment ranges.
 * Ranges are sorted by open offset, so the loop short-circuits as soon as it passes `lineStart`.
 */
function isInsideBlockComment(
  ranges: ReadonlyArray<[number, number]>,
  lineStart: number,
): boolean {
  for (const [start, end] of ranges) {
    if (start >= lineStart) {
      break;
    }
    if (end > lineStart) {
      return true;
    }
  }
  return false;
}

/**
 * Returns the index of the first character after the `//` line comment that starts at `from`,
 * or `docSlice.length` if no newline follows.
 */
function skipLineComment(docSlice: string, from: number): number {
  const newline = docSlice.indexOf('\n', from);
  return newline >= 0 ? newline + 1 : docSlice.length;
}

/**
 * Returns the index of the first character after the block comment whose `/*` opens at `from`,
 * or `docSlice.length` if the closing delimiter is absent.
 */
function skipBlockComment(docSlice: string, from: number): number {
  // Start search at from+2 to skip past the /* opener itself — otherwise /*/
  // would be misread as an immediately-closed comment (the * at [1] and / at [2]
  // would form a false */ match).
  const close = docSlice.indexOf('*/', from + 2);
  return close >= 0 ? close + 2 : docSlice.length;
}

/**
 * Returns the index of the first character after the double-quoted string that opens at `from`.
 * Handles `\"` escape sequences. Treats an unmatched `"` as ending at the next newline or end of
 * input — strings do not span lines in DQL.
 */
function skipStringLiteral(docSlice: string, from: number): number {
  let i = from + 1; // skip the opening "
  while (i < docSlice.length) {
    const ch = docSlice[i];
    if (ch === '\\') {
      i += 2; // skip escaped character
    } else if (ch === '"') {
      return i + 1; // past the closing "
    } else if (ch === '\n') {
      return i + 1; // string does not span lines; treat as implicitly closed
    } else {
      i++;
    }
  }
  return docSlice.length;
}

/**
 * Scans a single line left-to-right, skipping double-quoted string contents, and returns
 * the character offsets of the first real `/*`, `//`, and `[` tokens — where "real" means
 * outside a string literal. Returns `-1` for any token that is absent or only appears inside
 * a string.
 *
 * A plain `indexOf` call would incorrectly match tokens that appear inside string values such
 * as `filter url == "http://example.com"`, where `//` is not a line comment.
 */
function scanLineTokens(lineText: string): {
  blockComment: number;
  lineComment: number;
  bracket: number;
} {
  let i = 0;
  let blockComment = -1;
  let bracket = -1;
  while (i < lineText.length) {
    const ch = lineText[i];
    if (ch === '"') {
      i = skipStringLiteral(lineText, i);
      continue;
    }
    if (ch === '/' && lineText[i + 1] === '/') {
      // // ends the real content of this line; nothing after it matters
      return { blockComment, lineComment: i, bracket };
    }
    if (ch === '/' && lineText[i + 1] === '*' && blockComment < 0) {
      blockComment = i;
    }
    if (ch === '[' && bracket < 0) {
      bracket = i;
    }
    i++;
  }
  return { blockComment, lineComment: -1, bracket };
}

/**
 * Scans `docSlice` (which starts at `lineStart + bracketIdx` in the document) for the `]` that
 * closes the outermost `[`, skipping nested brackets, comment tokens, and string literals.
 * @returns The absolute document offset of the closing `]`, or `-1` if not found.
 */
function findMatchingBracketEnd(
  docSlice: string,
  lineStart: number,
  bracketIdx: number,
): number {
  // depth tracks nesting: the opening [ has already been consumed by the caller
  // (docSlice starts at that [), so the first [ increments depth to 1 and the
  // matching ] decrements it back to 0, which signals the end of the subquery.
  let depth = 0;
  let i = 0;
  while (i < docSlice.length) {
    const ch = docSlice[i];
    if (ch === '"') {
      i = skipStringLiteral(docSlice, i);
      continue;
    }
    if (ch === '/' && docSlice[i + 1] === '/') {
      i = skipLineComment(docSlice, i);
      continue;
    }
    if (ch === '/' && docSlice[i + 1] === '*') {
      i = skipBlockComment(docSlice, i);
      continue;
    }
    if (ch === '[') {
      depth++;
    } else if (ch === ']') {
      depth--;
      if (depth === 0) {
        return lineStart + bracketIdx + i;
      }
    }
    i++;
  }
  return -1;
}

/**
 * Returns a fold range for a multi-line block comment (opening `/*`) that starts on the given line,
 * or `null` if the line contains no such opener or the comment closes on the same line.
 * @param blockCommentIdx - Character offset of the `/*` on this line (outside any string literal
 *   and before any `//`), or `-1` if absent. Computed by {@link scanLineTokens}.
 */
function findBlockCommentFold(
  state: EditorState,
  lineStart: number,
  lineEnd: number,
  blockCommentIdx: number,
): { from: number; to: number } | null {
  if (blockCommentIdx < 0) {
    return null;
  }

  const docSlice = state.doc.sliceString(lineStart + blockCommentIdx);
  const closeOffset = docSlice.indexOf('*/', 2);
  if (closeOffset < 0) {
    return null;
  }

  const closeEnd = lineStart + blockCommentIdx + closeOffset + 2;
  // closeEnd points one past the closing /; subtract 1 to land on the * so
  // lineAt resolves the line that contains the */ delimiter, not the one after it.
  const closeLine = state.doc.lineAt(closeEnd - 1);
  if (closeLine.from <= lineEnd) {
    return null;
  }

  const closeLineText = state.doc.sliceString(closeLine.from, closeLine.to);
  const delimOffset = closeLineText.indexOf('*/');
  // Fold up to the * of */ (not the line start) so leading whitespace is hidden
  // and the result reads /*…*/ rather than /*… */.
  return {
    from: lineEnd,
    to: closeLine.from + (delimOffset >= 0 ? delimOffset : 0),
  };
}

/**
 * Returns a fold range for a multi-line subquery `[ … ]` that opens on the given line,
 * or `null` if the line contains no such opener or the closing `]` is on the same line.
 * @param bracketIdx - Character offset of the `[` on this line (outside any string literal
 *   and before any `//`), or `-1` if absent. Computed by {@link scanLineTokens}.
 */
function findSubqueryFold(
  state: EditorState,
  lineStart: number,
  lineEnd: number,
  bracketIdx: number,
): { from: number; to: number } | null {
  if (bracketIdx < 0) {
    return null;
  }

  const docSlice = state.doc.sliceString(lineStart + bracketIdx);
  const closePos = findMatchingBracketEnd(docSlice, lineStart, bracketIdx);
  if (closePos < 0) {
    return null;
  }

  const closeLine = state.doc.lineAt(closePos);
  if (closeLine.from <= lineEnd) {
    return null;
  }

  return { from: lineEnd, to: closePos };
}

/**
 * Provides foldable regions for DQL:
 *  - multi-line block comments
 *  - multi-line subqueries
 * Used with StreamLanguage which has no syntax tree and therefore no built-in fold ranges.
 * @internal
 */
export const dqlFoldService = [
  blockCommentRangesField,
  foldService.of((state, lineStart, lineEnd) => {
    if (isInsideBlockComment(state.field(blockCommentRangesField), lineStart)) {
      return null;
    }

    const lineText = state.doc.sliceString(lineStart, lineEnd);
    const { blockComment, bracket } = scanLineTokens(lineText);

    return (
      findBlockCommentFold(state, lineStart, lineEnd, blockComment) ??
      findSubqueryFold(state, lineStart, lineEnd, bracket)
    );
  }),
];
